import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { SubtoolVertex } from '@editor/interfaces/tool'
import { mergePolylines } from '@editor/pages/projects/editor/tools/Polyline/store/polyline'
import { usePolylineSelector } from '@editor/pages/projects/editor/tools/store/selector'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectInspectionItems, useVertexSelector } from '@editor/store/selectors'

/**
 * Hook for handling merging polylines functionality.
 */
const useMergingPolyline = (savedPolylines: InspectionItem[]) => {
  // Store
  const dispatch = useAppDispatch()
  const currentVertexSubtool = useVertexSelector((state) => state.currentVertexSubtool)
  const completedPolylines = usePolylineSelector((state) => state.polyline.completedPolylines)
  const editedPolylines = usePolylineSelector((state) => state.polyline.editedPolylines)
  const inspectionItems = useAppSelector(selectInspectionItems)
  const polylineWidths = useMemo(
    () => inspectionItems.filterByType(InspectionItemType.PolylineWidth),
    [inspectionItems],
  )

  // States
  const [hoveredAnchorForMerging, setHoveredAnchorForMerging] = useState<PointArray[]>()

  // Refs
  const selectedPolylineForMergingRef = useRef<{ id: string; pointIndex: number }>(undefined)

  // Derived
  const isMergingPolyline = currentVertexSubtool === SubtoolVertex.ConnectVertex

  /**
   * Get polyline by ID from either completed polylines or saved polylines.
   *
   * @param id ID of the polyline to fetch.
   */
  const getPolylineById = useCallback(
    (id: string) => {
      let polyline = completedPolylines.find((pl) => pl.id === id)
      if (!polyline) {
        const editedWidth = editedPolylines.find((pl) => pl.id === id)?.width
        const savedPolyline = savedPolylines.find((item) => item.inspection_item_id === id)

        if (savedPolyline) {
          polyline = {
            id: savedPolyline.inspection_item_id!,
            inspectionItemId: savedPolyline.inspection_item_id,
            groupId: savedPolyline.inspection_group_id,
            points: savedPolyline.polyline_length?.positions_for_distance || [],
            width:
              editedWidth ??
              polylineWidths.find((width) => width.polyline_length_id === savedPolyline.inspection_item_id)
                ?.polyline_width?.estimated_value ??
              undefined,
          }
        }
      }

      return polyline
    },
    [completedPolylines, editedPolylines, savedPolylines, polylineWidths],
  )

  /**
   * Handle merging of two polylines.
   *
   * @param polylineId ID of the polyline being merged into.
   * @param anchorIndex Index of the anchor point being used for merging.
   */
  const handleMergePolylines = useCallback(
    (polylineId: string, anchorIndex: number, point: PointArray) => {
      // -- First selection
      if (!selectedPolylineForMergingRef.current) {
        selectedPolylineForMergingRef.current = { id: polylineId, pointIndex: anchorIndex }
        setHoveredAnchorForMerging([point])
        return
      }

      // -- Unselecting the same point
      if (
        selectedPolylineForMergingRef.current.id === polylineId &&
        selectedPolylineForMergingRef.current.pointIndex === anchorIndex
      ) {
        selectedPolylineForMergingRef.current = undefined
        setHoveredAnchorForMerging(undefined)
        return
      }

      // -- Second selection
      // If the same polyline is selected, update selection instead
      if (selectedPolylineForMergingRef.current.id === polylineId) {
        selectedPolylineForMergingRef.current.pointIndex = anchorIndex
        setHoveredAnchorForMerging([point])
        return
      }

      // Fetch both polylines, abort if not found
      let firstPolyline = getPolylineById(selectedPolylineForMergingRef.current.id)
      let secondPolyline = getPolylineById(polylineId)
      if (!firstPolyline || !secondPolyline) {
        selectedPolylineForMergingRef.current = undefined
        setHoveredAnchorForMerging(undefined)
        return
      }

      // -- Need to make sure the first polyline is always the one with width defined
      // as the second is merged into first. If first does not have it and the second does,
      // the merged polyline will have no width.
      let ancIndex = anchorIndex
      if (firstPolyline.width === undefined && secondPolyline.width !== undefined) {
        const temp = firstPolyline
        firstPolyline = secondPolyline
        secondPolyline = temp

        const tempIndex = selectedPolylineForMergingRef.current.pointIndex
        selectedPolylineForMergingRef.current.pointIndex = ancIndex
        ancIndex = tempIndex
      }

      // -- Merge polylines
      // Prepare new points by merging the two polylines at the selected points
      const secondIndex = ancIndex
      let newPoints: PointArray[] = []

      // Add second polyline points.
      // Need to make sure the order is correct depending on the selected indices.
      const firstIndex = selectedPolylineForMergingRef.current.pointIndex
      const isFirstAtEnd = firstIndex === firstPolyline.points.length - 1
      const isSecondAtStart = secondIndex === 0

      if (isFirstAtEnd && isSecondAtStart) {
        // P1End -> P2Start = normal order
        newPoints = [...firstPolyline.points, ...secondPolyline.points]
      } else if (isFirstAtEnd && !isSecondAtStart) {
        // P1End -> P2End = reverse P2 before merging
        newPoints = [...firstPolyline.points, ...secondPolyline.points.slice().reverse()]
      } else if (!isFirstAtEnd && isSecondAtStart) {
        // P1Start -> P2Start = reverse P1 before merging
        newPoints = [...firstPolyline.points.slice().reverse(), ...secondPolyline.points]
      } else {
        // P1Start -> P2End = normal order but insert at beginning
        newPoints = [...secondPolyline.points, ...firstPolyline.points]
      }

      // -- Commit changes
      dispatch(
        mergePolylines({
          firstPolyline,
          secondPolyline,
          newPoints,
        }),
      )

      // -- Cleanups
      selectedPolylineForMergingRef.current = undefined
      setHoveredAnchorForMerging(undefined)
    },
    [getPolylineById, dispatch],
  )

  /**
   * Reset temp states when merging mode is exited.
   */
  useEffect(() => {
    if (!isMergingPolyline) {
      selectedPolylineForMergingRef.current = undefined
      setHoveredAnchorForMerging(undefined)
    }
  }, [isMergingPolyline])

  return {
    isMergingPolyline,
    selectedPolylineForMergingRef,
    handleMergePolylines,

    hoveredAnchorForMerging,
    setHoveredAnchorForMerging,
  }
}

export default useMergingPolyline
